import { useCallback, useMemo, useState } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { theme } from '@sg/garnish';

export const useTierUpgradeCelebrationModal = () => {
  const [containerHeight, setContainerHeight] = useState(0);
  const [headingTextHeight, setHeadingTextHeight] = useState(0);
  const [footerTextHeight, setFooterTextHeight] = useState(0);

  const transformMax = useMemo(() => {
    // Space to simulate padding on the top and bottom of the content
    const spaceVertical = theme.spacing.large * 2;

    // Remained height after subtracting the space
    const spacedArea = containerHeight - spaceVertical;

    // Available height for the animation
    const withTextInsets = spacedArea - headingTextHeight - footerTextHeight;

    // Available space for each text to move
    return withTextInsets / 2;
  }, [containerHeight, headingTextHeight, footerTextHeight]);

  const progress = useSharedValue(0);

  const headingTextAnimatedStyle = useAnimatedStyle(
    () => ({
      opacity: progress.value > APPEARANCE_TILL / 2 ? 1 : 0,
      transform: [
        {
          translateY:
            interpolate(progress.value, [0, APPEARANCE_TILL, 1], [0, 0, 1]) *
              -transformMax +
            IMAGE_HEIGHT / 2,
        },
      ],
    }),
    [progress, transformMax],
  );

  const opacityAnimatedStyle = useAnimatedStyle(
    () => ({
      opacity: interpolate(progress.value, [0, APPEARANCE_TILL, 1], [0, 0, 1]),
    }),
    [progress],
  );

  const footerTextAnimatedStyle = useAnimatedStyle(
    () => ({
      opacity: progress.value >= APPEARANCE_TILL ? 1 : 0,
      transform: [
        {
          translateY:
            interpolate(progress.value, [0, APPEARANCE_TILL, 1], [0, 0, 1]) *
              transformMax -
            IMAGE_HEIGHT / 2,
        },
      ],
    }),
    [progress, transformMax],
  );

  const onContentLayout = useCallback((e: LayoutChangeEvent) => {
    setContainerHeight(e.nativeEvent.layout.height);
  }, []);

  const onHeadingTextLayout = useCallback((e: LayoutChangeEvent) => {
    setHeadingTextHeight(e.nativeEvent.layout.height);
  }, []);

  const onFooterTextLayout = useCallback((e: LayoutChangeEvent) => {
    setFooterTextHeight(e.nativeEvent.layout.height);
  }, []);

  const startAnimation = useCallback(() => {
    const movementValue = withTiming(1, {
      duration: ANIMATION_DURATION,
    });
    const appearanceValue = withTiming(0.7, {
      duration: ANIMATION_DURATION,
    });

    // eslint-disable-next-line functional/immutable-data
    progress.value = withDelay(
      ANIMATION_DELAY,
      withSequence(appearanceValue, withDelay(ANIMATION_DELAY, movementValue)),
    );
  }, [progress]);

  const stopAnimation = useCallback(() => {
    // eslint-disable-next-line functional/immutable-data
    progress.value = 0;
  }, [progress]);

  return useMemo(
    () => ({
      startAnimation,
      stopAnimation,
      onContentLayout,
      onHeadingTextLayout,
      onFooterTextLayout,
      headingTextAnimatedStyle,
      opacityAnimatedStyle,
      footerTextAnimatedStyle,
    }),
    [
      startAnimation,
      stopAnimation,
      onContentLayout,
      onHeadingTextLayout,
      onFooterTextLayout,
      headingTextAnimatedStyle,
      opacityAnimatedStyle,
      footerTextAnimatedStyle,
    ],
  );
};

// ─── CONSTANTS ──────────────────────────────────────────────────────────────────────

export const APPEARANCE_TILL = 0.7;
export const ANIMATION_DURATION = 1000;
export const ANIMATION_DELAY = 300;
export const IMAGE_HEIGHT = 200;
